import React from "react";
import {
  Avatar,
  Box,
  Card,
  Chip,
  Skeleton,
  Typography,
  useTheme,
  Grid2
} from "@mui/material";

import { MdOutlineArrowOutward } from "react-icons/md";
import moment from "moment";
import {
  useGetEngineeringProgressKpiQuery,
  useGetEngineeringApprovalBottleneckQuery,
} from "store/slices/apis/engineeringDashboardSlice";
import { useParams } from "react-router-dom";
import { ArrowUpward, ArrowDownward } from "@mui/icons-material";
import ProjectStatusOverviewHead from "components/ProjectStatusOverviewHead";
import { ReactComponent as ArrowUpIcon } from "assets/PwC_Funct_Icons_ArrowUp_Outline_Black_RGB.svg"
import { ReactComponent as ArrowDownIcon } from "assets/PwC_Funct_Icons_ArrowDown_Outline_Black_RGB.svg"

const ProgressKPIEngg = () => {
  const theme = useTheme();
  const { projectId } = useParams();
  const { data } = useGetEngineeringApprovalBottleneckQuery({ projectId });

  const { data: RiskKPI = [], isLoading } = useGetEngineeringProgressKpiQuery({
    projectId,
  });

  const lastUpdated = RiskKPI?.lastUpdated;

  const kpiData = [
    {
      title: "Total Engineering Critical Activities",
      value: "totalCriticalActivities",
      change: "totalCriticalActivitiesChange",
      iconColor: "#E0F0E4",
      textColor: "#377E36",
      dotColor: "#CE412D",
      color: "#9ED3CC",
      timeFrame: "this month",
      status: "total",
    },
    {
      title: "Delay in Days",
      value: "delayInDays",
      change: "delayInDaysChange",
      iconColor: "#E0F0E4",
      textColor: "#377E36",
      dotColor: "#CE412D",
      color: "#9ED3CC",
      timeFrame: "this month",
      status: "delay",
    },
    {
      title: "Engineering Critical Activity Variance",
      value: "criticalPathVariance",
      change: "criticalPathVarianceChange",
      iconColor: "#E0F0E4",
      textColor: "#377E36",
      dotColor: "#CE412D",
      color: "#9ED3CC",
      timeFrame: "this month",
      status: "EngVariance",
    },
    {
      title: "Forecast Completion Date",
      value: "forecastCompletionDate",
      change: "forecastCompletionDateChange",
      isDate: true,
      valueColor: "#CE412D",
      iconColor: "#E0F0E4",
      textColor: "#377E36",
      dotColor: "#CE412D",
      color: "#9ED3CC",
    },
    {
      title: "Planned Completion Date",
      value: "plannedCompletionDate",
      isDate: true,
      valueColor: "#252525",
      iconColor: "#E0F0E4",
      textColor: "#377E36",
      dotColor: "#CE412D",
      color: "#9ED3CC",
    },
  ];

  const AvgData = [
    {
      title: "Average Time to Approve a Document",
      value: "avgApprovalTimeDays",
      iconColor: "#E0F0E4",
      isDate: true,
      textColor: "#377E36",
      dotColor: "#CE412D",
      color: "#9ED3CC",
    },
    {
      title: "Average Rejection Rate",
      change: "avgRejectionRatePercent",
      valueColor: "#252525",
      iconColor: "#E0F0E4",
      isDate: true,
      textColor: "#377E36",
      dotColor: "#CE412D",
      color: "#9ED3CC",
    },
    {
      title: "Average Revision Rate",
      value: "avgRevisionRate",
      valueColor: "#252525",
      iconColor: "#E0F0E4",
      isDate: true,
      textColor: "#377E36",
      dotColor: "#CE412D",
      color: "#9ED3CC",
    },
  ];

  const averageData = AvgData.map((item) => {
    let value, change, arrow;
    let isPositive = true;
    let absoluteChange;

    if (item.isDate) {
      value = data?.data[item.value] ?? 0
      change = "-";
      isPositive = true;
    } else {
      value = data?.data[item.value] ?? 0;
      const changeValue = value;
      arrow = changeValue >= 0 ? "up" : "down";
      isPositive = changeValue > 0;
      absoluteChange = isNaN(changeValue)
        ? "-"
        : changeValue === -100
          ? "0%"
          : `${Math.abs(changeValue)}%`;
    }

    return {
      ...item,
      value,
      change,
      isPositive,
      arrow,
      absoluteChange,
    };
  });

  const riskKPIData = kpiData.map((item) => {
    let value, change, arrow;
    let isPositive = true;
    let absoluteChange;

    if (item.isDate) {
      const rawValue = RiskKPI?.[item.value];
      value = rawValue ? moment(rawValue).format("D MMMM, YYYY") : "-";
      change = "-";
      isPositive = true;
    } else {
      value = RiskKPI?.[item.value];
      const changeValue = value;
      arrow = changeValue >= 0 ? "up" : "down";
      isPositive = changeValue > 0;
      absoluteChange = isNaN(changeValue)
        ? "-"
        : changeValue === -100
          ? "0%"
          : `${Math.abs(changeValue)}%`;
    }

    return {
      ...item,
      value,
      change,
      isPositive,
      arrow,
      absoluteChange,
    };
  });

  return (
    <>
      <Grid2 container rowSpacing={1.25} columnSpacing={3} sx={{ mt: "20px" }}>
        <ProjectStatusOverviewHead
          lastModified={lastUpdated}
          marginBottm={"20px"}
        />

        {[...riskKPIData, ...averageData].map((item, index) => (
          <Grid2 key={index} size={{ xs: 12, md: 3, lg: 3 }}>
            <Card
              sx={{
                boxShadow: "none",
                border: "1px solid #25252517",
                borderRadius: "3px",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                p={2}
              >
                <Box display="flex" alignItems="center">
                  <Typography
                    sx={{ bgcolor: item.color, width: 45, height: 45, mr: 2 }}
                  />
                  <Box>
                    <Box display="flex" alignItems="center">
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          mr: 1,
                          color: item.valueColor,
                          fontSize: "18px",
                          whiteSpace: "nowrap",
                          lineHeight: "100%",
                          letterSpacing: "-0.4px",
                        }}
                      >
                        {item.value}
                      </Typography>
                      {item.status && (
                        <Chip
                          label={item.absoluteChange}
                          size="small"
                          icon={
                            item.absoluteChange !== "0%" &&
                            // (item.arrow === "up" ? (
                            //   <ArrowUpward
                            //     sx={{
                            //       fontSize: "12px !important",
                            //       color: !item.isPositive
                            //         ? `${item.textColor} !important`
                            //         : `${item.dotColor} !important`,
                            //     }}
                            //   />
                            // ) : (
                            //   <ArrowDownward
                            //     sx={{
                            //       fontSize: "12px !important",
                            //       color: !item.isPositive
                            //         ? `${item.textColor} !important`
                            //         : `${item.dotColor} !important`,
                            //     }}
                            //   />
                            // ))
                            (item.arrow === "up" ? (
                              <ArrowUpIcon
                                style={{
                                  height: "12px",
                                  width: "12px",
                                  fill: !item.isPositive ? item.textColor : item.dotColor,
                                }}
                              />
                            ) : (
                              <ArrowDownIcon
                                style={{
                                  height: "12px",
                                  width: "12px",
                                  fill: !item.isPositive ? item.textColor : item.dotColor,
                                }}
                              />
                            ))

                          }
                          sx={{
                            backgroundColor: item.iconColor,
                            color: item.isPositive
                              ? item.dotColor
                              : item.textColor,
                            fontWeight: 500,
                            fontSize: "12px",
                            borderRadius: "4px",
                          }}
                        />
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          ml: "2px",
                          fontWeight: 400,
                          fontSize: "13px",
                          lineHeight: "24px",
                          letterSpacing: "-0.4px",
                          color: "#474747",
                        }}
                      >
                        {item.timeFrame}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: "5px",
                        lineHeight: "100%",
                        letterSpacing: "-0.4px",
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "#252525",
                      }}
                    >
                      {item.title}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Card>
          </Grid2>
        ))}
      </Grid2>
    </>
  );
};

export default ProgressKPIEngg;
